/* eslint-disable @typescript-eslint/no-empty-object-type */
import { useLazyUpdateState } from "@buzzeasy/shared-frontend-utilities";
import { SignalRHubV2 } from "@buzzeasy/shared-frontend-utilities/signalr";
import { PropsWithChildren, ReactElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { v4 as uuidV4 } from "uuid";
import environmentConfig from "../../../environmentConfig";
import { useGetMyTeamsQuery } from "../../../redux/apis/teamsApi";
import { selectAuthInfo } from "../../../redux/authSlice";
import { RawAgentLiveData, RawAgentTodayData, RawWorkItemData } from "../../models/reports.agents";
import { RawQueueLiveData, RawQueueTodayData } from "../../models/reports.queues";
import { Team } from "../../models/team";
import { SignalRConnectionContext, SignalRConnectionHandlers } from "./SignalRConnectionContext";

const { apiBaseUrl } = environmentConfig;

export default function SignalRConnectionProvider({ children }: PropsWithChildren): ReactElement {
  const { token, user: { id: userId } } = useSelector(selectAuthInfo);

  const { data: foundTeams } = useGetMyTeamsQuery(undefined, { pollingInterval: 60000 });

  // TODO: create useLazyMemo in shared-frontend-utilities because this looks ugly AF
  const [teams, setTeams] = useLazyUpdateState<Team[]>([]);
  useEffect(
    () => { setTeams(foundTeams ?? []); },
    [foundTeams, setTeams],
  );

  const queueIds = useMemo(
    () => Array.from(new Set(teams.flatMap(t => t.queueIds))),
    [teams],
  );

  const [subscribers, setSubscribers] = useState<Record<string, Partial<SignalRConnectionHandlers>>>({});

  // subscribersRef used not to tear down and rebuild SignalR connection every time on subscriber change
  const subscribersRef = useRef(subscribers);
  subscribersRef.current = subscribers;

  const subscribe = useCallback(
    (handlers: Partial<SignalRConnectionHandlers>) => {
      const subscriberId = uuidV4();
      setSubscribers(curr => ({ ...curr, [subscriberId]: handlers }));
      return subscriberId;
    },
    [],
  );

  const unsubscribe = useCallback(
    (subscriberId: string) => setSubscribers((curr) => {
      const newSubscribers = { ...curr };
      delete newSubscribers[subscriberId];
      return newSubscribers;
    }),
    [],
  );

  const receiveTodaysQueueData = useCallback(
    (data: RawQueueTodayData) => Object.values(subscribersRef.current).forEach(handlers => handlers.receiveTodaysQueueData?.(data)),
    [],
  );

  const receiveLiveQueueData = useCallback(
    (data: RawQueueLiveData) => Object.values(subscribersRef.current).forEach(handlers => handlers.receiveLiveQueueData?.(data)),
    [],
  );

  const receiveTodaysAgentData = useCallback(
    (data: RawAgentTodayData) => Object.values(subscribersRef.current).forEach(handlers => handlers.receiveTodaysAgentData?.(data)),
    [],
  );

  const receiveLiveAgentData = useCallback(
    (data: RawAgentLiveData[]) => Object.values(subscribersRef.current).forEach(handlers => handlers.receiveLiveAgentData?.(data)),
    [],
  );

  const receiveWorkItemData = useCallback(
    (data: RawWorkItemData[]) => Object.values(subscribersRef.current).forEach(handlers => handlers.receiveWorkItemData?.(data)),
    [],
  );

  useEffect(
    () => {
      if (queueIds.length > 0) {
        const todayHub = SignalRHubV2.create<{}, TodayHubClient>({ baseUrl: `${apiBaseUrl}/aggregations/today/hub`, queryParams: { queueIds, agentIds: [userId] } }, token);
        todayHub.on("ReceiveTodaysAgentReportData", receiveTodaysAgentData);
        todayHub.on("ReceiveTodaysQueueData", receiveTodaysQueueData);

        void todayHub.start();
        return () => { void todayHub.stop(); };
      }
    },
    [queueIds, receiveTodaysAgentData, receiveTodaysQueueData, token, userId],
  );

  useEffect(
    () => {
      if (queueIds.length > 0) {
        const liveHub = SignalRHubV2.create<{}, LiveHubClient>({ baseUrl: `${apiBaseUrl}/live/hub`, queryParams: { queueIds } }, token);
        liveHub.on("ReceiveQueueData", receiveLiveQueueData);
        liveHub.on("ReceiveTenantAgentStates", receiveLiveAgentData);
        liveHub.on("ReceiveWorkItemsWithConversations", receiveWorkItemData);

        void liveHub.start();
        return () => { void liveHub.stop(); };
      }
    },
    [queueIds, receiveLiveAgentData, receiveLiveQueueData, receiveWorkItemData, token],
  );

  return (
    <SignalRConnectionContext.Provider value={{ subscribe, unsubscribe, teams }}>
      {children}
    </SignalRConnectionContext.Provider>
  );
}

interface TodayHubClient {
  ReceiveTodaysAgentReportData(data: RawAgentTodayData): void;
  ReceiveTodaysQueueData(data: RawQueueTodayData): void;
}

interface LiveHubClient {
  ReceiveQueueData(data: RawQueueLiveData): void;
  ReceiveTenantAgentStates(data: RawAgentLiveData[]): void;
  ReceiveWorkItemsWithConversations(data: RawWorkItemData[]): void;
}
